/**
 * The session image, one of the two settings that is not a value but a
 * **document** — the other is what a session's agent is given (§5.26), and
 * `ExtensionsField.tsx` is its counterpart here for the same reason.
 *
 * It is why it kept ending up as JSON: a Dockerfile in a 288px column is a
 * Dockerfile nobody can read, so the row it lives on has no value column at
 * all and the editor underneath *is* the value.
 *
 * The grammar is the app's own — `parseDockerfile` from `@airlock/contracts` —
 * so what is refused here is exactly what would be refused on a machine, said
 * in the same words, before it is published rather than in a 422 afterwards.
 *
 * And it is **coloured**, which is not decoration. This is the one field in the
 * area that holds code, and an instruction Airlock does not replay — `COPY`,
 * `USER`, `WORKDIR` — is the mistake people actually make here. Colouring it
 * the moment it is typed says so at the point of typing; the list of issues
 * underneath then explains it rather than being the first anybody hears of it.
 * A `<pre>` behind a transparent `<textarea>` rather than an editor component:
 * this file is an editor for five instructions, and CodeMirror is 200 KB.
 */
import { useMemo, useRef } from 'react';
import type React from 'react';
import { BASE_DOCKERFILE, parseDockerfile } from '@airlock/contracts/settings/dockerfile';
import { color, font, radius } from '../../theme/tokens';

/** What Airlock replays. Anything else is either refused by name or unknown. */
const REPLAYED = new Set(['FROM', 'RUN', 'ENV', 'ARG', 'LABEL']);

/**
 * What it refuses by name, which is the list worth colouring: every one of
 * these is a real Dockerfile instruction that does nothing here, so somebody
 * typing one has a reasonable expectation it will work. `parseDockerfile` holds
 * the same names and the sentence for each; this only needs to know which they
 * are.
 */
const REFUSED = new Set([
  'COPY',
  'ADD',
  'CMD',
  'ENTRYPOINT',
  'USER',
  'WORKDIR',
  'EXPOSE',
  'VOLUME',
  'HEALTHCHECK',
  'SHELL',
  'ONBUILD',
  'STOPSIGNAL',
  'MAINTAINER'
]);

/** The two rules that have to hold identically on the text and on its shadow. */
const TEXT: React.CSSProperties = {
  margin: 0,
  padding: '12px 14px',
  border: 0,
  fontFamily: font.mono,
  fontSize: 12.5,
  lineHeight: 1.55,
  letterSpacing: 'normal',
  tabSize: 4,
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  wordBreak: 'normal'
};

export function DockerfileField({
  value,
  locked,
  onChange
}: {
  value: string;
  locked?: boolean;
  onChange: (value: string) => void;
}): React.JSX.Element {
  const issues = useMemo(() => parseDockerfile(value).issues, [value]);
  const changed = value.trimEnd() !== BASE_DOCKERFILE.trimEnd();
  const shadow = useRef<HTMLPreElement>(null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            fontFamily: font.mono,
            fontSize: 10.5,
            letterSpacing: '.12em',
            textTransform: 'uppercase',
            color: color.inkFaint
          }}
        >
          Session image
        </span>
        <span style={{ flex: 1, height: 1, background: color.hairSoft }} />
        {changed && !locked && (
          <button
            type="button"
            className="link-quiet"
            onClick={() => onChange(BASE_DOCKERFILE)}
            style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12, color: color.accentText }}
          >
            Restore default
          </button>
        )}
      </div>

      {/* The border and the background belong to the wrapper, so the two layers
          inside it are the same box to the pixel — a padding or a border on one
          of them and not the other is a caret that drifts a character further
          from its letter on every line. */}
      <div
        style={{
          position: 'relative',
          border: `1px solid ${issues.length ? color.badText : color.line}`,
          borderRadius: radius.md,
          background: locked ? color.surfaceAlt : color.shellPage,
          overflow: 'hidden'
        }}
      >
        <pre
          ref={shadow}
          aria-hidden="true"
          style={{
            ...TEXT,
            position: 'absolute',
            inset: 0,
            overflow: 'hidden',
            pointerEvents: 'none',
            color: color.ink
          }}
        >
          {colour(value)}
          {/* A trailing newline, so the last line of the shadow is not clipped
              when the text ends without one. */}
          {'\n'}
        </pre>
        <textarea
          value={value}
          readOnly={locked}
          spellCheck={false}
          rows={14}
          onChange={(event) => onChange(event.target.value)}
          onScroll={(event) => {
            if (shadow.current) shadow.current.scrollTop = event.currentTarget.scrollTop;
          }}
          style={{
            ...TEXT,
            position: 'relative',
            display: 'block',
            width: '100%',
            background: 'transparent',
            // The letters are drawn by the layer underneath; what the textarea
            // contributes is the caret, the selection and the typing.
            color: 'transparent',
            caretColor: color.ink,
            outline: 'none',
            resize: 'vertical'
          }}
        />
      </div>

      {issues.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {issues.map((issue, index) => (
            <li key={index} style={{ fontSize: 12, lineHeight: 1.45, color: color.badText }}>
              {typeof issue === 'string' ? issue : String((issue as { message?: string }).message ?? issue)}
            </li>
          ))}
        </ul>
      )}

      <p style={{ margin: 0, fontSize: 12, lineHeight: 1.45, color: color.inkFaint }}>
        Airlock replays these instructions once, into a snapshot every session then starts from. Editing it rebuilds that
        snapshot on the next session, so that one starts slowly — and removing <span style={{ fontFamily: font.mono }}>claude</span>{' '}
        stops sessions starting at all.
      </p>
    </div>
  );
}

/* ── the colouring ──────────────────────────────────────────────────────── */

/**
 * The whole document, a line at a time.
 *
 * Line by line and not by one regular expression over the text, because what
 * an instruction *is* depends on where it sits: `RUN` at the start of a line is
 * the instruction, and `RUN` inside a shell command is a word. A line that
 * continues the one above it — the one before ended in a backslash — is shell,
 * so nothing at its start is an instruction either.
 */
function colour(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let continuing = false;

  text.split('\n').forEach((line, index) => {
    if (index > 0) out.push('\n');
    out.push(<Line key={index} text={line} continuing={continuing} />);
    // A trailing backslash carries the instruction onto the next line; a
    // comment never does, whatever it ends with.
    continuing = !line.trimStart().startsWith('#') && /\\\s*$/.test(line);
  });
  return out;
}

function Line({ text, continuing }: { text: string; continuing: boolean }): React.JSX.Element {
  if (text.trimStart().startsWith('#')) {
    return <span style={{ color: color.inkGhost, fontStyle: 'italic' }}>{text}</span>;
  }

  const head = continuing ? null : /^(\s*)([A-Za-z][A-Za-z0-9_]*)(.*)$/s.exec(text);
  if (!head) return <>{shell(text)}</>;

  const [, indent, word, rest] = head;
  const instruction = word.toUpperCase();
  const tone = REPLAYED.has(instruction)
    ? { color: color.accentText, fontWeight: 600 }
    : REFUSED.has(instruction)
      ? { color: color.badText, fontWeight: 600, textDecoration: 'underline wavy' }
      : { color: color.warnText, fontWeight: 600 };

  return (
    <>
      {indent}
      <span style={tone}>{word}</span>
      {shell(rest)}
    </>
  );
}

/**
 * The rest of a line: what is quoted, what is a variable, and the backslash
 * that carries it on. Everything else is left alone — this is a shell command,
 * and colouring a shell is a job with no end.
 */
function shell(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const pattern = /("(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|\$\{[^}]*\}|\$[A-Za-z_][A-Za-z0-9_]*|\\\s*$)/g;
  let at = 0;

  for (let match = pattern.exec(text); match !== null; match = pattern.exec(text)) {
    if (match.index > at) out.push(text.slice(at, match.index));
    const piece = match[0];
    out.push(
      <span
        key={match.index}
        style={
          piece.startsWith('$')
            ? { color: color.warnText }
            : piece.startsWith('\\')
              ? { color: color.inkGhost }
              : { color: color.inkSubtle }
        }
      >
        {piece}
      </span>
    );
    at = match.index + piece.length;
  }
  if (at < text.length) out.push(text.slice(at));
  return out;
}
